// Utility to normalize differing trade dataset shapes into a common schema

// Common schema fields produced (alongside original fields):
// - symbol
// - open_time, close_time
// - open_price, close_price
// - entryPrice, exitPrice
// - pnl, volume, pips
// - commission, swap
// - stopLoss, takeProfit
// - duration
// - trade_side
// - account_id, positionId

export const normalizeTradeData = (trade) => {
  if (!trade || typeof trade !== "object") return null;

  // Start with a shallow copy so we preserve all original fields
  const original = { ...trade };

  // Detect dataset shape A: keys like symbol, open_time, close_time, open_price, close_price
  const isShapeA =
    ("symbol" in original && "open_time" in original) ||
    ("symbol" in original &&
      ("open_price" in original || "close_price" in original));

  // Detect dataset shape B: keys like contractName/symbolName, enteredAt/createdAt, entryPrice/exitPrice
  const isShapeB =
    ("contractName" in original || "symbolName" in original) &&
    ("enteredAt" in original || "createdAt" in original);

  // Base mappings with robust fallbacks
  const symbol =
    original.symbol ??
    original.contractName ??
    original.symbolName ??
    original.symbol_code ??
    null;
  const open_time =
    original.open_time ??
    original.enteredAt ??
    original.createdAt ??
    original.openedAt ??
    null;
  const close_time =
    original.close_time ?? original.exitedAt ?? original.closedAt ?? null;

  const open_price =
    original.open_price ??
    original.entryPrice ??
    original.entry_price ??
    original.openPrice ??
    null;
  const close_price =
    original.close_price ??
    original.exitPrice ??
    original.exit_price ??
    original.closePrice ??
    null;

  const pnl =
    original.pnl ??
    original.profit ??
    original.netProfit ??
    original.grossProfit ??
    0;
  const volume = original.volume ?? original.lots ?? original.quantity ?? null;
  const pips = original.pips ?? original.pipValue ?? null;
  const commission = original.commission ?? original.commissions ?? 0;
  const fee = original.fee ?? original.fees ?? 0;
  const swap = original.swap ?? original.swapValue ?? 0;

  const stopLoss =
    original.stop_loss ?? original.stopLoss ?? original.sl ?? null;
  const takeProfit =
    original.take_profit ?? original.takeProfit ?? original.tp ?? null;

  const rawDuration =
    original.trade_duration ??
    original.tradeDurationDisplay ??
    original.tradeDuration ??
    original.duration ??
    null;

  // Parse duration into seconds. Supported formats:
  // 1) "0 days 00:00:08"
  // 2) "00:00:24.6136830" or "HH:MM:SS"
  // 3) numeric seconds
  let durationSeconds = null;
  if (typeof rawDuration === "number") {
    durationSeconds = rawDuration;
  } else if (typeof rawDuration === "string") {
    const daysMatch = rawDuration.match(
      /(\d+)\s*days?\s*(\d{2}):(\d{2}):(\d{2})/i
    );
    if (daysMatch) {
      const days = parseInt(daysMatch[1], 10) || 0;
      const hours = parseInt(daysMatch[2], 10) || 0;
      const minutes = parseInt(daysMatch[3], 10) || 0;
      const seconds = parseInt(daysMatch[4], 10) || 0;
      durationSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
    } else {
      const hmsMatch = rawDuration.match(
        /^(\d{2}):(\d{2}):(\d{2})(?:[.,]\d+)?$/
      );
      if (hmsMatch) {
        const hours = parseInt(hmsMatch[1], 10) || 0;
        const minutes = parseInt(hmsMatch[2], 10) || 0;
        const seconds = parseInt(hmsMatch[3], 10) || 0;
        durationSeconds = hours * 3600 + minutes * 60 + seconds;
      }
    }
  }

  // Resolve trade side
  const numericAction =
    typeof original.action === "number" ? original.action : null;
  const derivedSideFromNumeric =
    numericAction === 1
      ? "BUY"
      : numericAction === -1 || numericAction === 2
      ? "SELL"
      : null;

  const trade_side =
    original.trade_side ||
    original.in_action ||
    original.action ||
    derivedSideFromNumeric ||
    (typeof original.positionSize === "number"
      ? original.positionSize > 0
        ? "BUY"
        : original.positionSize < 0
        ? "SELL"
        : null
      : null) ||
    original.direction ||
    null;

  // Resolve account and position identifiers
  const account_id =
    original.account_id ??
    original.tradingAccountId ??
    original.accountId ??
    null;
  const positionId =
    original.position ??
    original.positionId ??
    original.id ??
    original.tradeId ??
    null;

  // Provide duplicated conveniences for consumers that expect both naming styles
  const entryPrice = open_price;
  const exitPrice = close_price;

  // Prefer explicit symbol if shapes match, else inferred above
  const resolvedSymbol = isShapeA || isShapeB ? symbol : symbol;

  // MAE/MFE mappings and times
  const maePip = original.maePip ?? original.maxAdverseExcursionPip ?? null;
  const maePrice =
    original.maePrice ??
    original.maxAdverseExcursionPrice ??
    original.mae ??
    null;
  const maeTime = original.maeTime ?? original.maxAdverseExcursionTime ?? null;
  const mfePip = original.mfePip ?? original.maxFavorableExcursionPip ?? null;
  const mfePrice =
    original.mfePrice ??
    original.maxFavorableExcursionPrice ??
    original.mfe ??
    null;
  const mfeTime =
    original.mfeTime ?? original.maxFavorableExcursionTime ?? null;

  // Convenience date (defaults to open_time)
  const date = original.date ?? original.tradeDay ?? open_time ?? null;

  return {
    ...original,
    symbol: resolvedSymbol,
    open_time,
    close_time,
    open_price,
    close_price,
    entryPrice,
    exitPrice,
    pnl,
    volume,
    pips,
    commission: commission || fee,
    fee,
    swap,
    stopLoss,
    takeProfit,
    duration: rawDuration,
    durationSeconds,
    trade_side,
    account_id,
    positionId,
    maePip,
    maePrice,
    maeTime,
    mfePip,
    mfePrice,
    mfeTime,
    date,
  };
};

export const normalizeTradeArray = (trades) => {
  if (!Array.isArray(trades)) return [];
  return trades.map((t) => normalizeTradeData(t)).filter(Boolean);
};
